import { ReactComponent as BlockIcon } from '@assets/icons/V4/block-icon.svg';
import { PaginationProps } from '@components/V4/Pagination';
import Table, { SortOrder } from '@components/V4/Table/Table';
import { SearchMajorsDTO } from '@shared/clientTypes/DTOs/MajorsDTO';
import { Popconfirm } from 'antd';
import { useDeleteMajor, useRestoreMajor } from 'apps/agora/src/actions/major.actions';
import useToast from 'apps/agora/src/hooks/useToast';
import EmptyStateCard from 'apps/agora/src/views/UserView/components/EmptyStateCard';
import moment from 'moment';

interface MajorsTableProps {
  majors?: SearchMajorsDTO['majors'];
  areMajorsLoading: boolean;
  pagination: PaginationProps;
  sortBy: string | null;
  sortOrder: SortOrder | null;
  setSearchParams: (data: Partial<any>) => void;
  openMajorModalHandler: (majorId: string) => void;
}

const MajorsTable = (props: MajorsTableProps) => {
  const {
    majors,
    areMajorsLoading,
    setSearchParams,
    pagination,
    sortBy,
    sortOrder,
    openMajorModalHandler,
  } = props;

  const toast = useToast();

  const { mutate: deleteMajor } = useDeleteMajor({
    onSuccess: async () => {
      toast.success('Major deleted successfully.');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Major failed to delete.');
    },
  });

  const { mutate: restoreMajor } = useRestoreMajor({
    onSuccess: async () => {
      toast.success('Major restored successfully.');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Major failed to restore.');
    },
  });

  const sortHandler = (field: string, order: SortOrder) => {
    setSearchParams({
      sortBy: order ? field : null,
      sortOrder: order || null,
      page: 1,
    });
  };

  const deleteMajorHandler = (id?: string) => {
    if (!id) return;
    deleteMajor([id], {});
  };

  const restoreMajorHandler = (id?: string) => {
    if (!id) return;
    restoreMajor([id], {});
  };

  return areMajorsLoading ? (
    <div className="h-24 flex items-center justify-center text-customGrey">Loading...</div>
  ) : (
    <Table pagination={pagination} className="px-18 mb-20">
      <Table.Header>
        <Table.Row isHeader>
          <Table.HeaderCell
            onSort={(order) => sortHandler('name', order)}
            sortOrder={sortBy === 'name' ? sortOrder : undefined}
          >
            Major Name
          </Table.HeaderCell>
          <Table.HeaderCell
            onSort={(order) => sortHandler('createdAt', order)}
            sortOrder={sortBy === 'createdAt' ? sortOrder : undefined}
          >
            Created At
          </Table.HeaderCell>
          <Table.HeaderCell>Added By</Table.HeaderCell>
          <Table.HeaderCell>Updated At</Table.HeaderCell>
          <Table.HeaderCell>Modified By</Table.HeaderCell>
          <Table.HeaderCell>Actions</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {!areMajorsLoading && !majors?.length ? (
          <tr>
            <td colSpan={6} className="h-64">
              <EmptyStateCard
                icon={<BlockIcon className="text-customGrey" />}
                text={'No results.'}
              />
            </td>
          </tr>
        ) : (
          majors?.map((major) => (
            <Table.Row key={major._id}>
              <Table.Cell>{major.name}</Table.Cell>
              <Table.Cell>
                {major.createdAt ? moment(major.createdAt).format('DD.MM.YYYY') : 'n/a'}
              </Table.Cell>
              <Table.Cell>{major.addedBy?.fullName ? major.addedBy?.fullName : 'n/a'}</Table.Cell>
              <Table.Cell>
                {major.updatedAt ? moment(major.updatedAt).format('DD.MM.YYYY') : 'n/a'}
              </Table.Cell>
              <Table.Cell>
                {major.modifiedBy?.fullName ? major.modifiedBy?.fullName : 'n/a'}
              </Table.Cell>
              <Table.Cell>
                <div className="flex gap-2">
                  {!major?.isDeleted && (
                    <span
                      className="cursor-pointer underline"
                      onClick={() => openMajorModalHandler(major?._id ?? '')}
                    >
                      View
                    </span>
                  )}
                  {!major?.isDeleted ? (
                    <Popconfirm
                      onConfirm={() => deleteMajorHandler(major?._id)}
                      title={
                        <section className="max-w-110 flex flex-col gap-6">
                          <p>Are you sure you want to delete this Major?</p>
                        </section>
                      }
                    >
                      <span className="cursor-pointer underline text-customRed">Delete</span>
                    </Popconfirm>
                  ) : (
                    <span
                      onClick={() => restoreMajorHandler(major?._id)}
                      className="cursor-pointer underline "
                    >
                      Restore
                    </span>
                  )}
                </div>
              </Table.Cell>
            </Table.Row>
          ))
        )}
        {}
      </Table.Body>
    </Table>
  );
};

export default MajorsTable;
